/*
 * Copyright 2026 Sony Corporation
 */
import { Avatar, Box, Container, IconButton, ListItemText, Menu, MenuItem, Tooltip, Divider } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink, Outlet, useLocation, useMatch } from 'react-router-dom';

import { useAuth } from '../contexts/auth-context';
import { UsageProvider } from '../contexts/UsageProvider';

import { AdminIcon, DashboardIcon, DevicesIcon, ProjectsIcon } from './icons';
import { UsageBanner, UsageSummary } from './UsageBanner';

/** The width of the activity bar on the left edge. */
export const BAR_WIDTH = 48;

/** A page in the activity bar: an icon with its name as tooltip and accessible name; the current one is marked. */
const NavIcon = ({ to, icon, label }: { to: string; icon: ReactNode; label: string }) => {
  const { pathname } = useLocation();
  const current = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Tooltip title={label} placement="right">
      <IconButton
        component={RouterLink}
        to={to}
        aria-label={label}
        aria-current={current ? 'page' : undefined}
        sx={{
          width: BAR_WIDTH,
          height: BAR_WIDTH,
          borderRadius: 0,
          color: current ? 'primary.light' : 'text.secondary',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 8,
            bottom: 8,
            width: 2,
            bgcolor: current ? 'primary.main' : 'transparent',
          },
          '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
          '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -4 },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
};

/** Who is signed in: the initial of the e-mail; the menu shows the address and this month's token usage, and signs out. */
const Account = () => {
  const { email, signOut } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Tooltip title={email ?? 'Account'} placement="right">
        <IconButton
          aria-label="Account"
          aria-haspopup="menu"
          aria-expanded={anchor ? 'true' : undefined}
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{ width: BAR_WIDTH, height: BAR_WIDTH, borderRadius: 0 }}
        >
          <Avatar sx={{ width: 28, height: 28, fontSize: 14, bgcolor: 'primary.dark', color: 'primary.contrastText' }}>
            {(email ?? '?').charAt(0).toUpperCase()}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <ListItemText sx={{ px: 2, py: 0.5 }} secondary="Signed in as">
          {email}
        </ListItemText>
        <Divider />
        <UsageSummary />
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            signOut();
          }}
        >
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
};

/** The activity bar on the left edge (icons only, as in VS Code) and the page beside it. */
export const AppShell = () => {
  // A project fills the window with its panels; the other pages are a centred column.
  const onProject = useMatch('/projects/:projectId') !== null;
  const { isAdmin } = useAuth();

  return (
    <UsageProvider>
      <Box sx={{ height: '100dvh', display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
        <Box
          component="nav"
          aria-label="Main"
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: { xs: 'row', sm: 'column' },
            width: { sm: BAR_WIDTH },
            // A dark edge, as in VS Code, rather than the light divider of the panels.
            borderRight: { sm: '1px solid #000' },
            borderTop: { xs: '1px solid #000', sm: 'none' },
            bgcolor: 'background.paper',
          }}
        >
          <NavIcon to="/runtimes" icon={<DevicesIcon />} label="Runtimes" />
          <NavIcon to="/projects" icon={<ProjectsIcon />} label="Projects" />
          <NavIcon to="/dashboard" icon={<DashboardIcon />} label="Dashboard" />
          {isAdmin && <NavIcon to="/admin" icon={<AdminIcon />} label="Admin" />}
          <Box sx={{ flexGrow: 1 }} />
          <Account />
        </Box>
        {onProject ? (
          <Box
            component="main"
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              p: 2,
              pt: 1,
              overflow: { xs: 'auto', md: 'hidden' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <UsageBanner />
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Outlet />
            </Box>
          </Box>
        ) : (
          <Box component="main" sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto' }}>
            <UsageBanner />
            <Container maxWidth="lg" sx={{ py: 3 }}>
              <Outlet />
            </Container>
          </Box>
        )}
      </Box>
    </UsageProvider>
  );
};
